import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Form } from 'reactstrap'

import { API } from 'API'
import { UserAccessPermissionsEnum } from 'constants/userAccessPermissionsEnum'
import { RequestToasterContext } from 'containers/Providers'
import { getUserAuthorities } from 'core/auth'
import { ALL_ADMIN_DEPARTMENTS } from 'domains/user'

import { resolveDepartmentEditPolicy } from './utils/resolveDepartmentEditPolicy'

interface DepartmentsControlProps {
  username: string
  adminDepartments: string
  /** Access roles string of the currently logged-in (acting) user. */
  actorAccessRoles: string
  /** Access roles string of the target user being edited. */
  targetAccessRoles: string
  onUpdated?: () => void
}

const KNOWN_DEPARTMENTS = new Set<string>(ALL_ADMIN_DEPARTMENTS)

const parseDepartments = (value: string | null | undefined): string[] =>
  Array.from(
    new Set(
      (value ?? '')
        .split(',')
        .map((d) => d.trim())
        .filter((d) => KNOWN_DEPARTMENTS.has(d)),
    ),
  ).sort()

const areEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false
  return a.every((value, index) => value === b[index])
}

export default function DepartmentsControl({
  username,
  adminDepartments,
  actorAccessRoles,
  targetAccessRoles,
  onUpdated,
}: DepartmentsControlProps) {
  const { requestStatusRef } = useContext(RequestToasterContext)

  const [initialSelected, setInitialSelected] = useState<string[]>(() =>
    parseDepartments(adminDepartments),
  )
  const [selected, setSelected] = useState<string[]>(() => parseDepartments(adminDepartments))

  useEffect(() => {
    const initial = parseDepartments(adminDepartments)

    setInitialSelected(initial)
    setSelected(initial)
  }, [adminDepartments])

  const hasEditPermission = useMemo(
    () => getUserAuthorities().includes(UserAccessPermissionsEnum.SUPPORT_TOOL_EDIT_PERMISSIONS),
    [],
  )

  // Decide whether the acting user can edit this target's Access Departments and
  // whether multiple departments may be selected. Mirrors the backend
  // validateUpdateAdminDepartments rules.
  const { canEdit, allowMultiple } = useMemo(
    () => resolveDepartmentEditPolicy(actorAccessRoles, targetAccessRoles),
    [actorAccessRoles, targetAccessRoles],
  )

  const isReadOnly = !hasEditPermission || !canEdit

  const handleToggle = (department: string) => {
    if (isReadOnly) return

    setSelected((prev) => {
      const isChecked = prev.includes(department)

      if (allowMultiple) {
        const updated = isChecked ? prev.filter((d) => d !== department) : [...prev, department]

        return Array.from(new Set(updated)).sort()
      }

      // Single-selection mode: checking replaces the current selection,
      // unchecking the selected department clears it.
      return isChecked ? [] : [department]
    })
  }

  const isDirty = !areEqual(initialSelected, selected)
  // Button is disabled when there is no change or when all departments were removed.
  const isButtonDisabled = isReadOnly || !isDirty || selected.length === 0

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    if (isButtonDisabled) return

    API.updateAdminDepartments({ username, departments: selected })
      .then((status) => {
        if (status === 204 || status === 200) {
          requestStatusRef.current?.showAlert('Updated', 'success')
          setInitialSelected(selected)
          onUpdated?.()
        }
      })
      .catch(() => {
        requestStatusRef.current?.showAlert('Failed to update departments', 'danger')
      })
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div
        className="p-2 d-flex flex-column"
        style={{
          borderRadius: 4,
          gap: '0.5rem',
        }}>
        {ALL_ADMIN_DEPARTMENTS.map((department) => (
          <div key={department} className="form-check">
            <input
              type="checkbox"
              id={`department-${department}`}
              className="form-check-input"
              checked={selected.includes(department)}
              disabled={isReadOnly}
              readOnly={isReadOnly}
              onChange={() => handleToggle(department)}
            />
            <label className="form-check-label" htmlFor={`department-${department}`}>
              {department}
            </label>
          </div>
        ))}
      </div>

      {!isReadOnly && (
        <div className="d-flex justify-content-end mt-3" style={{ gap: '1rem' }}>
          <button type="submit" disabled={isButtonDisabled}>
            Update Departments
          </button>
        </div>
      )}
    </Form>
  )
}
